<template>
	<div class="application-container">
		<b-container fluid>
			<div class="main-content">
				<b-row>
					<b-col md="3" class="text-center">
						<div class="avatar">
							<Avatar :avatar="avatar.name"/>
							<div v-if="avatar.text" class="avatar-text">{{ avatar.text }}</div>
						</div>
					</b-col>
					<b-col md="5">
						<Step0
							v-if="step === 0"
							@continue="goToNextStep"
						/>
						<Step1
							v-if="step === 1"
							v-model="step1Data"
							:title="steps[step-1].text"
						/>
						<Step2
							v-if="step === 2"
							v-model="step2Data"
							:title="steps[step-1].text"
						/>
						<Step3
							v-if="step === 3 && step3Eligible"
							v-model="step3Data"
							:title="steps[step-1].text"
						/>
						<Step4
							v-if="step === realStep4"
							v-model="step4Data"
							:title="steps[step-1].text"
							:employment-modes="step1Data ? step1Data.employmentModes : undefined"
						/>
						<Step5
							v-if="step === realStep5 && step5Eligible"
							v-model="step5Data"
							:title="steps[step-1].text"
							:functions="step4Data ? step4Data.functions : undefined"
							:suppress-industrial-sector="isSuppressIndustrialSector"
							:organisational-area-eligible="isOrganisationAreaEligible"
						/>
						<Step6
							v-if="step === realStep6"
							v-model="step6Data"
							:title="steps[step-1].text"
						/>
						<Step7
							v-if="step === realStep7"
							:title="steps[step-1].text"
							:form-data="{
								step1: step1Data,
								step2: step2Data,
								step3: step3Data,
								step4: step4Data,
								step5: step5Data,
								step6: step6Data,
							}"
							@goBack="goToPreviousStep"
						/>
					</b-col>
					<b-col md="4">
						<div class="right-area">
							<ul v-if="step > 1" class="steps">
								<li
									v-if="!loopStep.hideInNav"
									v-for="loopStep in steps"
									:key="loopStep.number"
									:class="{'step': true, 'active': step === loopStep.number, 'done': step > loopStep.number}"
								>
									<template v-if="step > loopStep.number && !isLastStep">
										<a href="#" class="step-link" @click.prevent="goToStep(loopStep.number)">
											<span class="step-number">
												<Icon icon="checkmark"/>
											</span>
											<span class="step-text">{{ loopStep.navText || loopStep.text }}</span>
										</a>
									</template>
									<template v-else>
										<span class="step-number">
											<Icon v-if="step > loopStep.number" icon="checkmark"/>
											<span v-else>{{ loopStep.number }}</span>
										</span>
										<span class="step-text">{{ loopStep.navText || loopStep.text }}</span>
									</template>
								</li>
							</ul>
						</div>
					</b-col>
				</b-row>
			</div>
			<div v-if="step > 0 && !isLastStep" class="continue">
				<a href="#" :class="['to-next-step', { 'disabled': !canContinue }]" @click.prevent="goToNextStep">
					Weiter
					<span class="to-next-step-icon">
						<Icon icon="caret-right"/>
					</span>
				</a>
				<span class="current-step">{{ step }}</span>
				<a
					v-for="(previousStep, i) in previousSteps"
					:key="'previous-step-' + previousStep"
					href="#"
					:class="'previous-step index-' + i"
					@click.prevent="goToStep(previousStep)"
				>
					{{ previousStep }}
				</a>
			</div>
		</b-container>
	</div>
</template>
<script>
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step6 from "./Step6";
import Step7 from "./Step7";
import Icon from "@/vue/icon/Icon"
import _ from "lodash";
import {mapState} from "vuex";
import Avatar, {
	CAT_NOTEBOOK,
	CLIPBOARD,
	HOLDING_EURO,
	HOLDING_NOTHING,
	IDEA_LIGHT_BULB,
	WELCOME
} from "@/vue/common/image/Avatar.vue";
import {setHash} from "@/helper/Url";
import {scrollToTop} from "@/helper/Window";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";
import * as CareerLevel from "@/helper/characteristic/CareerLevel";
import {isSuppress} from "@/helper/characteristic/IndustrialSector";
import {isEligible} from "@/helper/characteristic/OrganisationalArea";
import Step0 from "@/vue/application/Step0.vue";

const STEPS = [
	{
		number: 1,
		text: 'Welche Job-Art möchtest du?',
		pageVisitLabel: 'Jobart + Arbeitszeiten',
	},
	{
		number: 2,
		text: 'Wo möchtest du arbeiten?',
		pageVisitLabel: 'Homeoffice + Wunschort',
	},
	{
		number: 3,
		text: 'Welche Berufsebene möchtest du?',
		pageVisitLabel: 'Berufsebene',
	},
	{
		number: 4,
		text: 'Wohin kann dich dein nächster Job führen?',
		navText: 'Dein nächster Job',
		pageVisitLabel: 'Wunschtätigkeit',
	},
	{
		number: 5,
		text: 'In welchem Bereich?',
		pageVisitLabel: 'Organisationsbereich + Branche',
	},
	{
		number: 6,
		text: 'Wer bist denn du?',
		pageVisitLabel: 'Emailadresse Eingabeaufforderung',
	},
	{
		number: 7,
		text: 'Yeah, geschafft!',
		hideInNav: true,
		pageVisitLabel: 'Infoseite nach Email-Eingabe',
	},
];

const AVATARS = {
	0: {
		name: CAT_NOTEBOOK
	},
	1: {
		name: HOLDING_EURO
	},
	2: {
		name: IDEA_LIGHT_BULB
	},
	3: {
		name: HOLDING_EURO
	},
	4: {
		name: HOLDING_NOTHING,
		text: 'Wähle gerne auch Jobtätigkeiten aus, in denen Du noch keine Berufserfahrung hast, aber die dich interessieren.',
	},
	5: {
		name: IDEA_LIGHT_BULB,
	},
	6: {
		name: CLIPBOARD
	},
	7: {
		name: WELCOME
	},
};

export default {

	components: {
		Step0,
		Avatar,
		Step1,
		Step2,
		Step3,
		Step4,
		Step5,
		Step6,
		Step7,
		Icon,
	},

	data ()
	{
		return {
			step: 1,
			step1Data: undefined,
			step2Data: undefined,
			step3Data: undefined,
			step4Data: undefined,
			step5Data: undefined,
			step6Data: undefined,
		};
	},

	mounted ()
	{
		Event.push(
			new EventData()
				.setEvent(Event.REGISTRATION_STEP)
				.setAction(_.first(this.steps)?.pageVisitLabel ?? ('Schritt 1'))
		);

		window.onhashchange = () =>
		{
			if (location.hash && location.hash.startsWith('#step-'))
			{
				const step = parseInt(location.hash.replace('#step-', ''));

				if (this.canGoToStep(step))
				{
					this.step = step;
				}
			}
			else
			{
				this.step = 1;
			}
		};
	},

	computed: {
		...mapState('meta', [
			'meta',
		]),
		previousSteps ()
		{
			return _.reverse(
				_.range(1, this.step, 1)
			);
		},
		isLastStep ()
		{
			return this.step === this.steps.length;
		},
		avatar ()
		{
			let step = this.step;

			if (!this.step3Eligible && step >= 3)
			{
				step++;
			}

			if (!this.step5Eligible && step >= 5)
			{
				step++;
			}

			return AVATARS[step];
		},
		steps ()
		{
			let steps = [];

			let number = 1;
			STEPS.forEach(step =>
			{
				if (step.number === 3 && !this.step3Eligible)
				{
					return true; // continue
				}

				if (step.number === 5 && !this.step5Eligible)
				{
					return true; // continue
				}

				steps.push({
					...step,
					number: number++
				})
			});

			return steps;
		},
		realStep4 ()
		{
			return this.getRealStep(4, false);
		},
		realStep5 ()
		{
			return this.getRealStep(5);
		},
		realStep6 ()
		{
			return this.getRealStep(6);
		},
		realStep7 ()
		{
			return this.getRealStep(7);
		},
		canContinue ()
		{
			if (this.isLastStep)
			{
				return false;
			}

			return this.canGoToStep(this.step + 1);
		},
		step3Eligible ()
		{
			if (!this.step1Data?.employmentModes)
			{
				return true;
			}

			return CareerLevel.isEligible(
				_.map(this.step1Data.employmentModes, 'item')
			);
		},
		step5Eligible ()
		{
			return !this.isSuppressIndustrialSector || this.isOrganisationAreaEligible;
		},
		isOrganisationAreaEligible ()
		{
			return this.step4Data?.functions && isEligible(
				this.step4Data.functions.map(item => item.item)
			);
		},
		isSuppressIndustrialSector ()
		{
			return isSuppress(this.meta, this.step4Data?.functions);
		}
	},

	methods: {
		getRealStep (step, checkStep5)
		{
			checkStep5 ??= true;

			if (!this.step3Eligible)
			{
				step--;
			}

			if (checkStep5 && !this.step5Eligible)
			{
				step--;
			}

			return step;
		},
		canGoToStep (step)
		{
			if (step === 1)
			{
				return true;
			}

			let allEligibleDataSets = {
				0: this.step1Data,
				1: this.step2Data,
				2: this.step3Data,
				3: this.step4Data,
				4: this.step5Data,
				5: this.step6Data
			};

			if (!this.step3Eligible)
			{
				delete allEligibleDataSets[2];
			}

			if (!this.step5Eligible)
			{
				delete allEligibleDataSets[4];
			}

			allEligibleDataSets = _.values(allEligibleDataSets);

			let toCheck = [];

			for (let i = 1; i < step; i++)
			{
				toCheck.push(allEligibleDataSets[i - 1]);
			}

			if (toCheck.length === 0)
			{
				return false;
			}

			return toCheck.length === _.filter(toCheck, data => data && data.valid).length;
		},
		goToNextStep ()
		{
			if (!this.canContinue)
			{
				return;
			}

			this.goToStep(this.step + 1);
		},
		goToPreviousStep ()
		{
			this.goToStep(this.step - 1);
		},
		goToStep (step)
		{
			const stepConfig = this.steps.find(s => s.number === step);

			Event.push(
				new EventData()
					.setEvent(Event.REGISTRATION_STEP)
					.setAction(stepConfig?.pageVisitLabel ?? ('Schritt ' + step))
			);

			this.step = step;

			setHash('step-' + step);

			this.$nextTick(() =>
			{
				scrollToTop();
			});
		},
	},
}
</script>
